import { ConnectionAvatar } from '@app/modules/social-export/components/ConnectionAvatar';
import { SocialPlatformIcon } from '@app/modules/social-export/components/SocialPlatformIcon';
import { useConnectionDisplayName } from '@app/modules/social-export/components/useConnectionDisplayName';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useSocialExportGetConnectionById } from '@mi-front/api-v2';
import { Stack, Typography } from '@mui/material';
import { UploadCardContextSeparator } from './UploadCardContextSeparator';
import { UploadCardEditIndicator } from './UploadCardEditIndicator';
import { UploadCardDto } from './useUploadCardActions';

interface UploadCardContextBarProps {
  upload: UploadCardDto;
}

function getEditFromUpload(upload: UploadCardDto): { name: string } | null {
  if ('edit' in upload && upload.edit) {
    return upload.edit;
  }
  return null;
}

/**
 * Top-bar context: "Platform • [avatar] Profile • [edit chip]" with consistent separators.
 * CMS-style platforms omit the avatar and only show a connection label when a custom name is set.
 */
export function UploadCardContextBar({ upload }: UploadCardContextBarProps) {
  const platform = socialPlatforms.get(upload.platform);
  // Generated OpenAPI types ObjectIds as `object`; runtime JSON values are strings.
  const connectionId = typeof upload.connectionId === 'string' ? upload.connectionId : undefined;
  const edit = getEditFromUpload(upload);

  const connectionQuery = useSocialExportGetConnectionById(connectionId ?? '', {
    query: { enabled: !!connectionId },
  });

  const connection = connectionQuery.data;
  const resolvedDisplayName = useConnectionDisplayName(connection, platform);
  // Platforms without a user profile never fall back to a generic default in this bar.
  const connectionLabel = !platform.hasUserProfile
    ? connection?.name || null
    : resolvedDisplayName || null;
  const showConnectionAvatar = Boolean(connection) && platform.hasUserProfile;

  return (
    <Stack direction="row" alignItems="center" gap={1.5} minWidth={0} flexWrap="wrap" useFlexGap>
      <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
        <SocialPlatformIcon platform={platform} size="lg" />
        <Typography variant="subtitle2" color="text.secondary" noWrap>
          {platform.name}
        </Typography>
      </Stack>
      {connectionLabel && connection ? (
        <>
          <UploadCardContextSeparator />
          <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
            {showConnectionAvatar ? (
              <ConnectionAvatar
                connection={connection}
                platform={platform}
                sx={{ width: 20, height: 20, typography: 'caption' }}
              />
            ) : null}
            <Typography variant="subtitle2" color="text.secondary" noWrap component="span">
              {connectionLabel}
            </Typography>
          </Stack>
        </>
      ) : null}
      {edit ? (
        <>
          <UploadCardContextSeparator />
          <UploadCardEditIndicator edit={edit} />
        </>
      ) : null}
    </Stack>
  );
}
